// Аналитика онборда: 2 канала — Я.Метрика goals + BetterStack events.
// Я.Метрика — для верхнего funnel и UTM-привязки в директе.
// BetterStack — для structured events с campaignId/channelUUID/duration, SQL-запросы.

const YM_COUNTER = 96625866
// Same-origin proxy на нашем backend (B: POST /api/lk-events) вместо прямого
// betterstackdata.com. Причина — домен *.betterstackdata.com в стандартных
// tracker-blocklist'ах uBlock/Brave/Firefox tracking-protection, у 50-80%
// юзеров прямой fetch дропался и funnel был слеп. Now — свой домен, adblock
// молчит. Backend делает enrichment (serverReceivedAt, clientIP) и
// fire-and-forget форвардит в BetterStack. Токен BetterStack теперь на бэке
// в env, из клиента ушёл.
const BETTERSTACK_INGEST = '/api/lk-events'
const CAMPAIGN_STORAGE_KEY = 'tgx-magic-campaign'
const SESSION_STORAGE_KEY = 'tgx-analytics-session'

// Хеш бандла из vite (см. vite.config.ts → define) — позволяет в SQL-аналитике
// отделять события от старой версии после деплоя (фильтр по buildId).
declare const __BUILD_ID__: string
const BUILD_ID = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'

// In-memory user-context — выставляется из AuthContext/ChannelContext, добавляется
// ко всем track()-событиям. Не персистится — провайдеры перевыставят на mount.
interface UserContext {
  maxAdminID?: string
  maxChannelID?: string
  channelTitle?: string
}
const ctx: UserContext = {}

export function setUserContext(patch: Partial<UserContext>): void {
  Object.assign(ctx, patch)
}

declare global {
  interface Window {
    ym?: (counter: number, action: string, ...args: unknown[]) => void
  }
}

interface BasePayload {
  event: string
  level?: 'info' | 'warn' | 'error'
  message?: string
  channelUUID?: string
  campaignId?: string
  [k: string]: unknown
}

/**
 * Сохраняем campaignId из magic-link на всю сессию — каждое последующее событие
 * автоматически прикрепит его. Вызывается из AuthProvider после успешного /magic-login.
 */
export function rememberCampaign(campaignId?: string | null): void {
  if (!campaignId) return
  try {
    sessionStorage.setItem(CAMPAIGN_STORAGE_KEY, campaignId)
  } catch {}
}

function currentCampaign(): string | undefined {
  try {
    return sessionStorage.getItem(CAMPAIGN_STORAGE_KEY) || undefined
  } catch {
    return undefined
  }
}

function currentChannel(): string | undefined {
  try {
    return localStorage.getItem('tgx-selected-channel-uuid') || undefined
  } catch {
    return undefined
  }
}

/**
 * Стабильный id внутри одной вкладки — позволяет в SQL сгруппировать события
 * одного прохождения (включая случаи, когда juzer не залогинен).
 */
function currentSession(): string {
  try {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (cached) return cached
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36)
    sessionStorage.setItem(SESSION_STORAGE_KEY, id)
    return id
  } catch {
    return 'no-session'
  }
}

/**
 * v3.1a §6.1: campaignId стемпается на все `poll_*`/`polls_*` события — из-за
 * этого funnel-analysis Polls показывает атрибуцию к последней daily-comments
 * кампании (например `daily-comments-2026-05-27`). Marketing видит инфлированный
 * ROAS на не-poll-related кампанию. Гейт исключает Polls-события из
 * campaignId-стемпа — Polls funnel будет чистым, для его атрибуции при
 * необходимости заведём отдельный `sessionStorage['tgx-polls-campaign']`.
 *
 * v3.1b-1: реальная точка stamp — строка 107 в v3, здесь она сместилась после
 * добавления комментария. Функционально то же — гейт применяется на слое
 * enrichment внутри `sendToBetterstack`.
 */
function isPollEvent(event: string): boolean {
  return event.startsWith('poll_') || event.startsWith('polls_')
}

function sendToBetterstack(payload: BasePayload): void {
  // Не блокируем UI — fire-and-forget. Ошибки сети молча проглатываем,
  // аналитика не должна ломать продукт.
  const isPoll = isPollEvent(payload.event)
  const enriched = {
    dt: new Date().toISOString(),
    ...payload,
    level: payload.level || 'info',
    // BetterStack рендерит поле message в колонке Message — без него лента
    // выглядит пустой. По умолчанию дублируем туда event.
    message: payload.message || payload.event,
    channelUUID: payload.channelUUID || currentChannel(),
    // v3.1a §6.1: для poll_*/polls_* НЕ стемпаем campaignId из
    // sessionStorage['tgx-magic-campaign'] — иначе daily-comments/onboarding
    // кампания заражает Polls funnel. Явный `payload.campaignId` уважаем
    // (используется если polls когда-нибудь получат свои campaigns).
    campaignId: payload.campaignId || (isPoll ? undefined : currentCampaign()),
    maxAdminID: ctx.maxAdminID,
    maxChannelID: ctx.maxChannelID,
    channelTitle: ctx.channelTitle,
    sessionId: currentSession(),
    buildId: BUILD_ID,
    url: window.location.pathname + window.location.search,
    userAgent: navigator.userAgent,
  }
  try {
    fetch(BETTERSTACK_INGEST, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(enriched),
      keepalive: true, // переживает unload — полезно при отправке перед redirect
    }).catch(() => {})
  } catch {}
}

function sendYmGoal(goalName: string): void {
  if (typeof window.ym === 'function') {
    try {
      window.ym(YM_COUNTER, 'reachGoal', goalName)
    } catch {}
  }
}

/**
 * Главный метод трекинга. Шлёт в обе системы:
 * — `ym(reachGoal, event)` для воронки в Метрике
 * — POST в BetterStack для SQL-аналитики
 */
export function track(event: string, props: Omit<BasePayload, 'event'> = {}): void {
  sendYmGoal(event)
  sendToBetterstack({ event, ...props })
}

/**
 * Ошибка — событие с level=error и стектрейсом.
 */
export function trackError(err: unknown, context?: string, extra?: Record<string, unknown>): void {
  // Большинство API-ошибок прилетают как Error с code/status/body (см. api/auth.ts:31-37
  // и аналогично в creatives.ts) — автоматически вытаскиваем их.
  // requestId — UUID из X-Request-Id header'a (cheap-request-id, см. контракт §4).
  // Бэк пишет тот же id в свои логи; sql-сшивка фронт↔бэк работает.
  const e = err as {
    message?: string
    stack?: string
    name?: string
    code?: string
    status?: number
    body?: unknown
    requestId?: string
  }
  sendToBetterstack({
    event: 'error',
    level: 'error',
    message: e?.message || String(err),
    errorName: e?.name,
    errorCode: e?.code,
    errorStatus: e?.status,
    errorBody: e?.body,
    requestId: e?.requestId,
    stack: e?.stack?.slice(0, 4000), // обрезаем чтоб не раздуть payload
    context,
    ...extra,
  })
}
